// Runs a native background-upload job to completion, for the flowKinds that
// still run this way - `attendee-submit` (in-page "Submit Photos") and
// `drive-download-upload` (the Google Drive picker relay). Every other native
// upload flow (organiser initial-grouping/add-photos, guest upload-link
// manual, and now organiser-photo-submit - the organiser's own direct upload
// into the Photo Submissions panel) moved to the WorkManager pipeline (see
// CmUploadWorker.kt/the project plan) - native now does that entire pipeline
// itself, independent of this JS module and of any Activity/WebView, which is
// what actually fixes the class of bug this file's own resume model could
// never fully close (a killed process/WebView had no reliable way to keep
// running JS at all). `attendee-submit`/`drive-download-upload` are
// deliberately left on this older, JS-driven model (short/foreground-typical
// flows, low value from full backgrounding) - this function is what the
// headless WebView (FolderSyncForegroundService's onTaskRemoved, via
// SyncWorkerPage.js) and each flow's own button handler both call, so "the
// app got closed" and "the app is still open" stay one code path instead of
// two.
//
// `organiser-submit` (the legacy predecessor of organiser-photo-submit) is no
// longer CREATED by any call site as of the upload/grouping-decoupling change -
// its case in the switch below is kept only so a job already in flight from an
// older app version can still finish; remove it once a release has passed with
// no such jobs left.
//
// `deps` supplies the handful of engine functions that live in
// EventGallery/EventPage.js, passed in rather than imported directly to avoid
// a circular import (that file already imports from this module's siblings).
import { Capacitor } from '@capacitor/core';
import eventApi from '../../pages/EventGallery/eventApi';
import * as stagingProvider from './stagingProvider';
import { nativeUploadService } from './nativeUploadService';
import { downloadAndStageDriveFiles, rebuildDownloadedDriveFiles } from './driveDownloadStage';
import { PRIVACY_POLICY_VERSION } from '../../config/appConfig';

const CANCEL_POLL_MS = 2000;

// Guards against the SAME jobId being executed by two overlapping calls at
// once. This is a real scenario purely from SPA navigation, no app close/
// backgrounding needed at all: pressing Back to /dashboard and returning to
// the same event remounts EventPage's components fresh, with a brand new
// (empty) resumedJobIdsRef and zero memory of the still-running original
// call - its reconciliation effect sees the job as merely "incomplete" and
// immediately starts a SECOND concurrent runUploadJob against the exact same
// manifest/staged files as the first, still-running one. Confirmed on a real
// device: navigating away and back mid-upload (no close, no background)
// knocked the job back to "Preparing…"/"Grouping server is starting…" even
// though nothing about the OS process changed at all. This Set is
// module-level (not component state) so it survives exactly this kind of
// remount within the same page load, unlike a component's own useRef.
const activeJobIds = new Set();

export async function runUploadJob(job, deps = {}) {
  const { jobId, flowKind } = job;
  if (activeJobIds.has(jobId)) {
    return 'already-running';
  }
  activeJobIds.add(jobId);
  const {
    freshFiles, // optional: [{file, relativePath}] - only ever present on a fresh (never-yet-run) job
    runOrganiserUploadBatches, // drive-download-upload only
    isGroupingCancelledError,
  } = deps;

  // Cancellation support for this JS-driven pipeline (attendee-submit/
  // drive-download-upload) - CmUploadWorker's own WorkManager jobs check a
  // similar flag natively at each file/batch boundary; this is the twin for a
  // job that instead runs as plain JS, possibly with no live component (a
  // resumed-on-reopen run has none) to hold a component-owned cancel ref for.
  // Polls nativeUploadService.isUploadJobCancelled - set by the
  // notification's own "Cancel" action (FolderSyncForegroundService.
  // requestCancelUploadJob) - and, once true, flips the exact same
  // uploadCancelledRef/uploadAbortRef shape runOrganiserUploadBatches/the
  // in-app Cancel button already use, so every checkpoint that already
  // existed for a LIVE component's own cancel now also fires for a
  // notification-triggered one. No-ops entirely on web/PWA (this whole
  // pipeline is native-only to begin with).
  const cancelledRef = { current: false };
  const abortRef = { current: null };
  const cancelPollId = Capacitor.isNativePlatform() ? setInterval(async () => {
    if (cancelledRef.current) return;
    const cancelled = await nativeUploadService.isUploadJobCancelled(jobId).catch(() => false);
    if (cancelled) {
      cancelledRef.current = true;
      abortRef.current?.abort();
    }
  }, CANCEL_POLL_MS) : null;

  let outcome = 'failed';
  try {
    const manifest = await stagingProvider.readManifest(jobId);
    if (!manifest) return outcome; // already cleaned up (e.g. finished between the list check and now)
    const flowMeta = manifest.flowMeta || {};
    const eventId = manifest.eventId;

    if (flowKind === 'drive-download-upload') {
      if ((manifest.phase || 'downloading') === 'downloading') {
        await downloadAndStageDriveFiles(jobId, manifest, flowMeta.accessToken, (completed, total) => {
          nativeUploadService.updateJobProgress({ jobId, completedFiles: completed, totalFiles: total, phase: 'downloading' });
        }, () => cancelledRef.current);
        await stagingProvider.setPhase(jobId, 'uploading');
      }
      const latestManifest = await stagingProvider.readManifest(jobId);
      const entries = await rebuildDownloadedDriveFiles(jobId, latestManifest);
      let uploadedSoFar = (manifest.files || []).filter((f) => f.uploaded).length;
      const onFileUploaded = (relativePath) => {
        stagingProvider.markUploaded(jobId, [relativePath]);
        uploadedSoFar += 1;
        nativeUploadService.updateJobProgress({ jobId, completedFiles: uploadedSoFar, totalFiles: entries.length, phase: 'uploading' });
      };
      // relativePath -> driveFileId, so the upload engines' own existing
      // setDriveOriginalRefs bookkeeping (for "download uncompressed
      // original" later) keeps working exactly as it does for the old
      // client-token Drive flow - just built from the manifest instead of a
      // React ref, since this runs outside any component.
      const driveFileIdByPathRef = { current: {} };
      (latestManifest.driveFiles || []).forEach((f) => { driveFileIdByPathRef.current[f.name] = f.driveFileId; });
      const event = await eventApi.getEvent(eventId);
      // createDriveJob stores exactly one of these, matching which mode
      // started the job (see EventPage.js's runDriveDownloadAndUpload /
      // runDriveDownloadAndUploadAdd) - normalize both into the one payload
      // shape runOrganiserUploadBatches expects regardless of mode.
      const mode = flowMeta.payload ? 'initial' : 'add';
      const payload = flowMeta.payload || {
        source_directory: flowMeta.opts?.sourceDirLabel, source_mode: flowMeta.opts?.sourceModeOverride, limit: 0,
        model: flowMeta.opts?.faceModel, credit_hold_id: flowMeta.opts?.creditHoldId,
        organizer_terms_ack: true, policy_version: PRIVACY_POLICY_VERSION,
      };
      await runOrganiserUploadBatches(event, entries, mode, payload, {
        hooks: { onFileUploaded }, driveFileIdByPathRef, uploadCancelledRef: cancelledRef, uploadAbortRef: abortRef,
      });
      outcome = 'success';
      return outcome;
    }

    let loaded;
    if (freshFiles) {
      loaded = freshFiles; // fresh job - use the real in-memory Files directly, staged as they compress
    } else {
      // attendee-submit/organiser-submit are the only flowKinds that reach
      // here (see this file's own doc comment) - their createJob calls never
      // pre-populate `entries`/`resumeSource` (only CmUploadWorker's native
      // jobs do that now), so every manifest entry here was always added by
      // stageFile (i.e. always already staged) - a simple eager load of the
      // staged-but-unconfirmed tail is the complete resume story for this flow.
      const pending = (manifest.files || []).filter((f) => f.staged && !f.uploaded);
      const loadedEntries = await Promise.all(pending.map(async (entry) => {
        try {
          return { file: await stagingProvider.loadStagedFile(jobId, entry), relativePath: entry.relativePath };
        } catch {
          // Staged bytes missing despite the manifest saying "not yet
          // uploaded" - almost certainly means the file really did already
          // upload successfully (see stagingProvider.js's withManifestLock);
          // treat it as delivered rather than crashing the entire resume over
          // one file that can't be recovered either way.
          return null;
        }
      }));
      loaded = loadedEntries.filter(Boolean);
    }
    const totalFiles = manifest.totalFiles || loaded.length;
    let uploadedSoFar = (manifest.files || []).filter((f) => f.uploaded).length;
    let compressedSoFar = (manifest.files || []).filter((f) => f.staged).length;
    const onFileCompressed = (relativePath, file, originalRelativePath) => {
      stagingProvider.stageFile(jobId, relativePath, file, originalRelativePath);
      compressedSoFar += 1;
      // Compression alone can take a while on a large batch - without this the
      // notification sat frozen at "0 / N uploaded" the whole time, which on a
      // real device read as "nothing is happening" even though it plainly was.
      nativeUploadService.updateJobProgress({ jobId, completedFiles: compressedSoFar, totalFiles, phase: 'compressing' });
    };
    const onFileUploaded = (relativePath) => {
      stagingProvider.markUploaded(jobId, [relativePath]);
      uploadedSoFar += 1;
      nativeUploadService.updateJobProgress({ jobId, completedFiles: uploadedSoFar, totalFiles, phase: 'uploading' });
    };

    switch (flowKind) {
      case 'attendee-submit':
      case 'organiser-submit': {
        if (cancelledRef.current) throw new Error('Upload cancelled.');
        const form = new FormData();
        loaded.forEach(({ file, relativePath }) => form.append('files', file, relativePath));
        const controller = new AbortController();
        abortRef.current = controller;
        await eventApi.uploadPhotoSubmissions(eventId, form, controller.signal, { onFileCompressed, onFileUploaded });
        break;
      }
      default:
        throw new Error(`Unknown flowKind: ${flowKind}`);
    }
    outcome = 'success';
  } catch (err) {
    // Re-thrown after cleanup below so the caller (the button handler that
    // started a fresh job, or nothing at all for a silent resume) still sees
    // the failure and can react - e.g. show an error snack, restore billing
    // state - exactly as it did before this function existed.
    outcome = (isGroupingCancelledError?.(err) || err?.message === 'Upload cancelled.') ? 'cancelled' : 'failed';
    throw err;
  } finally {
    if (cancelPollId) clearInterval(cancelPollId);
    activeJobIds.delete(jobId);
    await nativeUploadService.completeJob({ jobId, outcome });
    await stagingProvider.deleteJob(jobId).catch(() => {});
  }
  return outcome;
}
